/**
 * useChatSystem — owns all freeform chat wiring for Variant C.
 *
 * Extracted from VariantCShell.tsx (Phase 4 refactor).
 *
 * Owns:
 *   - chatDocxPath state + effects (resolve on post-run, clear on idle)
 *   - useUserInputBridge (WebSocket approval bridge)
 *   - useSelectionCapture (captured selection for context pill)
 *   - useChatBridge (the core chat bridge)
 *   - handleSendChat adapter
 *   - useSelectionCitations
 */

import type { Dispatch } from "react";
import { useCallback, useEffect, useState } from "react";
import { classifyChatIntent, getRunStatus } from "../../api/client";
import { useUserInputBridge } from "../../hooks/useUserInputBridge";
import { detectCursorSectionNumberWithFallback, isValidLibraryId, readCursorAnchor } from "../../services/wordWriter";
import type { MentionAttachment } from "../../types";
import {
  type ForcedIntent,
  type RouteAction,
  type RouteContext,
  canTargetSection,
  decideForcedRoute,
  decideRoute,
  forcedRouteUnavailableMessage,
  withClassifierFallback,
} from "../chatIntentRouter";
import type { LoomFreshSelection } from "../components/LoomFresh";
import type { ResolvedScope } from "../cursorScope";
import type { RunIdentity } from "../sseAdapter";
import type { RunState, VariantCEvent } from "../state";
import { type ChatDispatchTarget, buildStreamingStatus, useChatBridge } from "./useChatBridge";
import { type CapturedSelection, useSelectionCapture } from "./useSelectionCapture";
import { useSelectionCitations } from "./useSelectionCitations";
import type { SpineSection } from "./useVariantCRun";

/** Paragraph-walk cap for the cursor-section hint on a document-wide send
 *  (same as the scope chip's). */
const CHAT_SCOPE_MAX_WALK = 40;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Asthra's steady reply when the writer tries to chat with NO source library
 *  configured (typically a custom submission that skipped the library step).
 *  Asthra only reasons over trusted source data, so it declines every chat turn
 *  until a library is attached and points the writer at the two ways to do it. */
export const NO_LIBRARY_CHAT_REFUSAL =
  "The agents only act on trusted source data, so I can't help here yet. " +
  "Select one of your available libraries, or create a new library to add your sources, and I'll get to work.";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

export interface UseChatSystemParams {
  runState: RunState;
  runId: string | null;
  documentSessionId: string | null;
  activeSelection: LoomFreshSelection | null;
  selectedLibraryId: string | null;
  identity: RunIdentity | null;
  restoredConversationId: string | undefined;
  sections: SpineSection[];
  dispatch: Dispatch<VariantCEvent>;
  /** Shell hook: the writer asked for NEW content to add to the cursor
   *  section — hand off to the in-request drafting stream. Not a run: the
   *  result lands as an Approve/Reject card for insertion at the cursor. */
  onFragmentDraftRequested?: (req: {
    runId: string;
    moduleId: string;
    sectionNumber: string | null;
    instruction: string;
    anchorText: string | null;
    attachments?: MentionAttachment[];
  }) => void;
  /** LLM intent routing on chat submit — on unless the
   *  `chat_intent_routing_disabled` kill switch is set. */
  intentRoutingEnabled?: boolean;
  /** Shell hook: a chat-initiated regen/ad-hoc run started for this module —
   *  drives the live drafting-progress bubble in the chat thread. */
  onChatRunActivity?: (
    activity: {
      moduleId: string;
      kind: "regen" | "adhoc";
      instruction: string;
    } | null,
  ) => void;
  /** Shell hook: the writer asked to edit the highlighted text — hand off
   *  to the highlight-edit flow (proposal card with Approve/Reject). */
  onHighlightEditRequested?: (req: {
    selectedText: string;
    instruction: string;
    runId: string | null;
    sectionId: string | null;
    sectionNumber: string | null;
    /** Writer-attached source assets (@-mentions) riding on the message. */
    attachments?: MentionAttachment[];
  }) => void;
}

// ---------------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------------

/** Options for a send whose route is already known — the writer picked a
 *  purpose-built action (a chip, or the composer's pinned edit mode). */
export interface ChatSendOptions {
  /** Route straight to this flow: no classifier.
   *  When the context can't support it the send is refused with a
   *  writer-readable error instead of silently falling back to chat. */
  forceIntent?: ForcedIntent;
  /** Highlight text to use instead of the captured selection. */
  selectedText?: string;
}

export interface UseChatSystemResult {
  chat: ReturnType<typeof useChatBridge>;
  handleSendChat: (
    text: string,
    scope: ResolvedScope,
    quotedMessage?: import("../messageProjection").QuotedMessage,
    attachments?: MentionAttachment[],
    options?: ChatSendOptions,
  ) => Promise<string | null>;
  /** Stop the in-flight chat run — aborts the fetch, denies any pending
   *  approval, and posts a "you stopped the run" notice. Wired to the
   *  composer's Stop button. */
  handleStopChat: () => void;
  selectionCitations: ReturnType<typeof useSelectionCitations>;
  captureSelection: () => Promise<void>;
  capturedSelection: CapturedSelection | null;
  dismissSelection: () => void;
  /** Forward the selection-chip probe's text into the captured selection. */
  syncSelectionFromProbe: (text: string | null) => void;
  pendingApproval: ReturnType<typeof useUserInputBridge>["pendingApproval"];
  submitApproval: ReturnType<typeof useUserInputBridge>["submitApproval"];
  rejectApproval: ReturnType<typeof useUserInputBridge>["rejectApproval"];
  bridgeStatus: ReturnType<typeof useUserInputBridge>["status"];
  buildStreamingStatus: typeof buildStreamingStatus;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useChatSystem(params: UseChatSystemParams): UseChatSystemResult {
  const {
    runState,
    runId,
    documentSessionId,
    activeSelection,
    selectedLibraryId,
    identity,
    restoredConversationId,
    sections,
    dispatch,
    intentRoutingEnabled,
    onChatRunActivity,
    onHighlightEditRequested,
    onFragmentDraftRequested,
  } = params;

  // -- chatDocxPath -------------------------------------------------------
  // Resolved on post-run by fetching /api/runs/{runId}. Immutable for
  // a given run so we only fetch once and skip refetches.
  const [chatDocxPath, setChatDocxPath] = useState<string | null>(null);

  useEffect(() => {
    if (runState !== "post-run" || !runId || chatDocxPath) return;
    let cancelled = false;
    getRunStatus(runId)
      .then((status) => {
        if (cancelled) return;
        if (status.docx_path) setChatDocxPath(status.docx_path);
      })
      .catch((err) => {
        if (typeof console !== "undefined") {
          console.info("[useChatSystem] chat docx_path resolution failed", err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [runState, runId, chatDocxPath]);

  // Reset path when the writer explicitly starts over.
  useEffect(() => {
    if (runState === "idle") setChatDocxPath(null);
  }, [runState]);

  // -- WebSocket bridge ---------------------------------------------------
  const userInputBridge = useUserInputBridge(documentSessionId || undefined);

  // -- Selection capture --------------------------------------------------
  const {
    captured: capturedSelection,
    captureNow: captureSelection,
    dismiss: dismissSelection,
    syncFromProbe: syncSelectionFromProbe,
  } = useSelectionCapture();

  // -- Chat bridge -------------------------------------------------------
  const chat = useChatBridge({
    docxPath: chatDocxPath,
    // From the run's ``run_metadata`` event — live and sandbox runs both emit
    // it, so there is no disk-scan fallback any more.
    trial: identity?.trial ?? undefined,
    session: identity?.session ?? undefined,
    docType: activeSelection?.backendDocType,
    libraryId: activeSelection?.libraryId ?? (selectedLibraryId || undefined),
    runId,
    connectionId: userInputBridge.connectionId,
    bridgeStatus: userInputBridge.status,
    initialConversationId: restoredConversationId,
    useAgentV3: false,
    onEvent: useCallback(
      (entry) => {
        dispatch({ type: "EVENT_APPENDED", entry });
      },
      [dispatch],
    ),
  });

  // -- Stop the chat run --------------------------------------------------
  // Wired to the composer's Stop button. Three concerns, in order:
  //   1. abortChat() drops the /chat-stream fetch → the backend SSE handler
  //      sees the disconnect and cancels the tool loop (no more rounds /
  //      approval prompts).
  //   2. rejectApproval() denies any approval prompt already on screen
  //      (no-op when none is pending) so it disappears immediately.
  //   3. emit an ``agent_note`` bubble intimating the writer stopped it.
  const handleStopChat = useCallback(() => {
    chat.abortChat();
    userInputBridge.rejectApproval();
    dispatch({
      type: "EVENT_APPENDED",
      entry: {
        ts: Date.now(),
        actor: "asthra",
        sectionId: null,
        runId: runId ?? null,
        payload: { kind: "agent_note", text: "You stopped the run." },
      },
    });
  }, [chat, userInputBridge, dispatch, runId]);

  // -- handleSendChat ----------------------------------------------------
  const handleSendChat = useCallback(
    async (
      text: string,
      scope: ResolvedScope,
      quotedMessage?: import("../messageProjection").QuotedMessage,
      attachments?: MentionAttachment[],
      options?: ChatSendOptions,
    ): Promise<string | null> => {
      const forceIntent = options?.forceIntent;
      // No source library → Asthra does no chat work. It only acts on trusted
      // source data, so any session without a library selected (in practice a
      // custom submission that skipped the library step) can't be reasoned
      // over. Echo the writer's prompt so the thread reflects what they asked,
      // then reply with a steady refusal pointing them at the two ways to add
      // sources. Runs before the intent-routing paths so nothing
      // downstream (which all assume a library) fires.
      const hasSourceLibrary = isValidLibraryId(activeSelection?.libraryId) || isValidLibraryId(selectedLibraryId);
      if (!hasSourceLibrary) {
        const promptTs = Date.now();
        dispatch({
          type: "EVENT_APPENDED",
          entry: {
            ts: promptTs,
            actor: "you",
            sectionId: null,
            runId,
            payload: { kind: "prompt", text, attachments },
          },
        });
        dispatch({
          type: "EVENT_APPENDED",
          entry: {
            ts: promptTs + 1,
            actor: "asthra",
            sectionId: null,
            runId,
            payload: { kind: "agent_note", text: NO_LIBRARY_CHAT_REFUSAL },
          },
        });
        return null;
      }

      
      let enrichedScope = scope;
      let cursorSectionNumber: string | undefined;
      if (scope.kind === "document") {
        // Only a hint for the reply: bookmarks first, then a short walk. The
        // uncapped walk (one sync per paragraph, up to 500) stalled Word on
        // documents without section bookmarks.
        const cursorNumber = await detectCursorSectionNumberWithFallback(undefined, {
          maxWalk: CHAT_SCOPE_MAX_WALK,
        });
        if (cursorNumber) {
          cursorSectionNumber = cursorNumber;
          enrichedScope = {
            kind: "cursor",
            sectionNumber: cursorNumber,
            label: `§${cursorNumber}`,
            hint: `Cursor is in §${cursorNumber}.`,
          };
        }
      }
      let sectionNumber =
        enrichedScope.kind === "inspector" || enrichedScope.kind === "cursor"
          ? enrichedScope.sectionNumber
          : cursorSectionNumber;
      // A forced route (chip / pinned composer mode) acts on the section the
      // cursor is in RIGHT NOW — the same click-time read the other section
      // chips use — not the ambient scope (which may be an inspected section
      // or a stale cursor value). One Word read, on an explicit action.
      const selectedText = quotedMessage
        ? undefined
        : options?.selectedText?.trim() || capturedSelection?.text || undefined;
      // A highlight lives in the document, so its section is read from Word at
      // send time and beats the stored scope (an inspected section, a stale
      // cursor value, or the post-run "first drafted section" fallback — the
      // title page — that made a §9.1 selection target "Title Page").
      if (forceIntent || selectedText) {
        const actionSection = await detectCursorSectionNumberWithFallback();
        if (actionSection) sectionNumber = actionSection;
      }
      const sectionId = sectionNumber ? (sections.find((s) => s.number === sectionNumber)?.id ?? null) : null;
      const target: ChatDispatchTarget = {
        sectionNumber,
        sectionId,
        selectedText,
        quotedMessage,
        attachments,
      };

      // -- Intent routing (kill switch: chat_intent_routing_disabled) ----
      // Fast Haiku classify, then route to a purpose-built flow. Every
      // failure path lands in plain chat — the classifier client never
      // throws, and decideRoute re-validates the context client-side.
      // A forced intent (chip / pinned composer mode) skips the classifier
      // and routes even when classification is switched off — the writer
      // named the action — but still goes through decideRoute's checks.
      if (intentRoutingEnabled || forceIntent) {
        const sectionStatus = sectionNumber ? (sections.find((s) => s.number === sectionNumber)?.status ?? null) : null;
        const routeCtx: RouteContext = {
          runId,
          runState,
          sectionId,
          sectionNumber: sectionNumber ?? null,
          selectedText: selectedText ?? null,
          sectionStatus,
        };
        let action: RouteAction;
        if (forceIntent) {
          const forced = decideForcedRoute(forceIntent, routeCtx, text);
          if (!forced) return forcedRouteUnavailableMessage(forceIntent);
          action = forced;
        } else {
          const intentResp = await classifyChatIntent({
            message: text,
            has_highlight: Boolean(selectedText),
            highlight_preview: selectedText ? selectedText.slice(0, 500) : undefined,
            cursor_section_number: sectionNumber ?? undefined,
            can_target_section: canTargetSection(routeCtx),
            doc_type: activeSelection?.backendDocType || undefined,
          });
          // If the classifier never answered (client timeout / transport
          // error) a highlight + imperative rewrite still routes to the
          // edit flow — same heuristic the server applies before its LLM.
          action = decideRoute(withClassifierFallback(intentResp, routeCtx, text), routeCtx, text);
        }
        // The intent that will ACTUALLY run (decideRoute may downgrade
        // the server's pick to plain chat when the client context no
        // longer supports it) — stamped on the prompt EventLog entry so
        // the chip in the thread reflects the executed action, and
        // carried on the dispatch target so the streaming status line
        // keeps the label for the chat route.
        const classifiedIntent: import("../../api/client").ChatRoutedIntent =
          action.kind === "chat" ? (action.mode === "research" ? "research_highlight" : "chat") : action.kind;
        target.intent = classifiedIntent;

        // Routed (non-chat) actions emit the writer's prompt entry here —
        // chat.sendChat won't run, so nothing else records it.
        const emitPromptEntry = () => {
          dispatch({
            type: "EVENT_APPENDED",
            entry: {
              ts: Date.now(),
              actor: "you",
              sectionId,
              runId,
              payload: {
                kind: "prompt",
                text,
                selectionContext: selectedText || undefined,
                intent: classifiedIntent,
                attachments,
              },
            },
          });
        };

        if (action.kind === "draft_content" && onFragmentDraftRequested) {
          emitPromptEntry();
          // Where the fragment goes. Read HERE, at send time, while the
          // cursor still means what the writer meant — the draft takes
          // 15-45s and they will have moved by the time it lands. One
          // on-demand Word read; the add-in keeps no ambient cursor state
          // (polling it froze Word on long drafts). A failed read is not
          // worth failing the draft over: a null anchor appends.
          let anchorText: string | null = null;
          if (action.sectionNumber) {
            try {
              anchorText = (await readCursorAnchor(action.sectionNumber))?.paragraphText ?? null;
            } catch (err) {
              console.warn("[useChatSystem] readCursorAnchor failed — appending instead:", err);
            }
          }
          // No run to dispatch, so no run stream to reattach and no
          // chatRunActivity to feed — the drafting flow owns its own
          // "requesting" phase and status line now. (chatRunActivity is only
          // ever cleared by a terminal RUN event; leaving it set here would
          // freeze the working bubble forever.)
          onFragmentDraftRequested({
            runId: action.runId,
            moduleId: action.sectionId,
            sectionNumber: action.sectionNumber,
            instruction: action.instruction,
            anchorText,
            attachments,
          });
          dismissSelection();
          return null;
        }

        if (action.kind === "edit_highlight" && onHighlightEditRequested) {
          emitPromptEntry();
          onHighlightEditRequested({
            selectedText: action.selectedText,
            instruction: action.instruction,
            runId: action.runId,
            sectionId: action.sectionId,
            sectionNumber: action.sectionNumber,
            attachments,
          });
          dismissSelection();
          return null;
        }

        if (action.kind === "chat" && action.mode === "research") {
          target.mode = "research";
        }
        // A forced route whose flow isn't wired must not leak into chat.
        if (forceIntent) return forcedRouteUnavailableMessage(forceIntent);
      }

      const result = await chat.sendChat(text, enrichedScope, target);
      dismissSelection();
      return result;
    },
    [
      chat,
      capturedSelection,
      dismissSelection,
      sections,
      runId,
      runState,
      dispatch,
      intentRoutingEnabled,
      activeSelection,
      selectedLibraryId,
      onChatRunActivity,
      onHighlightEditRequested,
      onFragmentDraftRequested,
    ],
  );

  // -- Selection citations -----------------------------------------------
  const selectionCitations = useSelectionCitations({
    trial: identity?.trial ?? undefined,
    session: identity?.session ?? undefined,
    docType: activeSelection?.backendDocType ?? "",
  });

  return {
    chat,
    handleSendChat,
    handleStopChat,
    selectionCitations,
    captureSelection,
    capturedSelection,
    dismissSelection,
    syncSelectionFromProbe,
    pendingApproval: userInputBridge.pendingApproval,
    submitApproval: userInputBridge.submitApproval,
    rejectApproval: userInputBridge.rejectApproval,
    bridgeStatus: userInputBridge.status,
    buildStreamingStatus,
  };
}
